import React, {useCallback, useEffect, useState} from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Stack,
  SxProps,
  Box,
  Skeleton,
  Divider,
} from "@mui/material";
import {SearchIcon, CancelIcon} from "@components/icons"; // Assuming SearchIcon is available in @components/icons
import {Theme} from "@emotion/react";
import {useDownBreakpoint} from "@hooks";
import {translate} from "@util";
import {useThemeContext} from "@theme";
import {api} from "@core/api";
import {Pageable, PageRequest, SearchResult} from "@core/model";
import {EmptyData} from "@components/EmptyData";
import {SearchResultsTable} from "./SearchResultsTable";
import {useNavigate} from "react-router-dom";
import {random} from "lodash";

export type SearchType = "asset" | "task" | "document" | "ticket" | "artifact";

export type SearchParams = {
  text: string;
  types: SearchType[];
};

type AdvancedSearchProps = {
  sx?: SxProps<Theme>;
};

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({sx}) => {
  const isDownSm = useDownBreakpoint("sm");
  const {theme} = useThemeContext();
  const navigate = useNavigate();

  const [results, setResults] = useState<Pageable<SearchResult>>();
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<
    Record<SearchType, boolean>
  >({
    asset: false,
    task: false,
    document: false,
    ticket: false,
    artifact: false,
  });

  const [currentParams, setCurrentParams] = useState<
    (SearchParams & PageRequest) | null
  >(null);

  const {data, isLoading, isFetching, isUninitialized} = api.useSearchQuery(
    currentParams!,
    {
      skip: !currentParams,
    },
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    handleClear();
    setOpen(false);
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTypes({
      ...selectedTypes,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSearch = useCallback(() => {
    const types = (Object.keys(selectedTypes) as SearchType[]).filter(
      type => selectedTypes[type],
    );

    setCurrentParams({
      text: searchText,
      page: 0,
      size: 20, // Default size or large enough
      sort: "name,ASC", // Default sort
      types: types,
    });
  }, [searchText, selectedTypes]);

  useEffect(() => {
    setResults(data);
  }, [data]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText.length >= 3) {
        handleSearch();
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText, handleSearch]);

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentParams(prev => {
      if (!prev) {
        return null;
      } else {
        return {
          ...prev,
          page: page,
          size: size,
        };
      }
    });
  };

  const handleRowClick = (item: SearchResult) => {
    handleClose();
    if (item.type === "TASK") {
      navigate(`/dashboard/tasks/day/${item.id}`);
    } else {
      navigate(`/dashboard/${item.type.toLowerCase()}s/${item.id}`);
    }
  };

  const handleClear = () => {
    setResults(undefined);
    setSearchText("");
    setSelectedTypes({
      asset: false,
      task: false,
      document: false,
      ticket: false,
      artifact: false,
    });
  };

  const loading = isLoading || isFetching;
  const ready = !loading && !isUninitialized && results;
  const empty = ready && results.content.length === 0;

  return (
    <>
      <IconButton onClick={handleOpen} sx={sx}>
        <SearchIcon color="inherit" />
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={isDownSm}
        scroll="paper"
        fullWidth
        maxWidth="md">
        <DialogTitle align="center">
          <Box
            bgcolor={theme.palette.secondary.dark}
            width="64px"
            borderRadius="32px"
            height="64px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            margin="0 auto"
            mb={2}>
            <SearchIcon
              fontSize="large"
              sx={{
                color: theme.palette.getContrastText(
                  theme.palette.secondary.dark,
                ),
              }}
            />
          </Box>
          {translate("userActions.search.title")}
          {isDownSm && (
            <IconButton
              onClick={handleClose}
              sx={{position: "absolute", right: 8, top: 8}}>
              <CancelIcon />
            </IconButton>
          )}
        </DialogTitle>
        <Stack
          spacing={1}
          mt={1}
          mx={2}
          flex={"1 1 0"}
          minHeight={!isDownSm ? "50vh" : "auto"}
          maxHeight={!isDownSm ? "50vh" : "none"}>
          <TextField
            autoFocus
            margin="dense"
            id="search-text"
            placeholder={translate("userActions.search.placeholder")}
            type="text"
            fullWidth
            variant="outlined"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTypes.asset}
                  onChange={handleTypeChange}
                  name="asset"
                />
              }
              label={translate("userActions.search.type.asset")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTypes.task}
                  onChange={handleTypeChange}
                  name="task"
                />
              }
              label={translate("userActions.search.type.task")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTypes.document}
                  onChange={handleTypeChange}
                  name="document"
                />
              }
              label={translate("userActions.search.type.document")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTypes.ticket}
                  onChange={handleTypeChange}
                  name="ticket"
                />
              }
              label={translate("userActions.search.type.ticket")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTypes.artifact}
                  onChange={handleTypeChange}
                  name="artifact"
                />
              }
              label={translate("userActions.search.type.artifact")}
            />
          </FormGroup>

          <Divider />
          {loading && (
            <Stack flex={"1 1 0"}>
              {Array.from({length: 5}).map((_, index) => (
                <Stack
                  key={`search-result-${index}`}
                  direction={"row"}
                  gap={2}
                  height={40}
                  alignItems={"center"}>
                  <Skeleton variant="circular" height={24} width={24} />
                  <Skeleton variant="text" width={random(100, 200)} />
                </Stack>
              ))}
            </Stack>
          )}
          {empty && <EmptyData />}
          {!empty && ready && (
            <SearchResultsTable
              data={results}
              onRowClick={handleRowClick}
              onPageChange={p =>
                handlePaginationChange(p, currentParams?.size || 20)
              }
              onRowsPerPageChange={r =>
                handlePaginationChange(currentParams?.page || 0, r)
              }
            />
          )}
          <Divider sx={{mt: "0 !important"}} />
        </Stack>
        <DialogActions>
          <Button onClick={handleClear} color="error">
            {translate("common.action.reset")}
          </Button>
          <Button onClick={handleSearch} variant="contained" color="primary">
            {translate("userActions.search.button")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export {AdvancedSearch};
